import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Owns the Weekly Notes panel's save/delete flow (text state, dirty check,
// the 2 fetch calls, and the local phasesDescriptions patch after each one)
// - mirrors useWeekNotesData owning the read side, so WeekNotesPanel.js
// itself stays presentational.
export const useWeekNoteEditor = ({
  selectedWeek,
  weekComment,
  athleteId,
  organizationCoachId,
  setPhasesDescriptions,
  onSaved,
  onDeleted,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [noteText, setNoteText] = useState(weekComment?.phaseDescription ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Navigating to a different week must show that week's own note, not
  // carry over whatever was being typed for the previous one.
  useEffect(() => {
    setNoteText(weekComment?.phaseDescription ?? "");
    setErrorMessage(null);
  }, [weekComment]);

  const isBlank = noteText.trim().length === 0;
  const isDirty = noteText !== (weekComment?.phaseDescription ?? "");
  // Blank is only a valid save when there's an existing note to delete via
  // it - a brand-new week with nothing but whitespace typed in has no note
  // to submit yet, so Save must stay disabled instead of firing a blank POST.
  const canSave = isDirty && !isSaving && (!isBlank || Boolean(weekComment));

  const dateAttachment = moment(selectedWeek).format("YYYYMMDD");

  const upsertLocalDescription = (saved) => {
    setPhasesDescriptions((previous) => [
      ...(previous || []).filter(
        (description) =>
          Number(description.dateAttachment) !== Number(saved.dateAttachment)
      ),
      saved,
    ]);
  };

  const removeLocalDescription = () => {
    setPhasesDescriptions((previous) =>
      (previous || []).filter(
        (description) =>
          Number(description.dateAttachment) !==
          Number(weekComment.dateAttachment)
      )
    );
  };

  const deleteNote = async () => {
    const params = new URLSearchParams({
      dateAttachment: weekComment.dateAttachment,
      athleteId,
    });
    if (organizationCoachId) {
      params.set("organizationCoachId", organizationCoachId);
    }
    const response = await fetch(
      `/actions/annual-planner/json/week-note?${params}`,
      {
        method: "DELETE",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || t("common:error"));
    }
    removeLocalDescription();
  };

  const saveNote = async () => {
    const response = await fetch("/actions/annual-planner/json/week-note", {
      method: weekComment ? "PUT" : "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        phaseDescription: noteText,
        dateAttachment,
        athleteId,
        ...(organizationCoachId ? { organizationCoachId } : {}),
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.error || t("common:error"));
    }
    upsertLocalDescription(data.newPhaseDescription);
  };

  // No separate Delete button (per product) - saving blank text on an
  // existing note deletes it instead. canSave already keeps a brand-new
  // week's blank/whitespace text from ever reaching this at all, so the
  // only way isBlank is true here is a real delete-via-blank-save on an
  // existing note - confirm is always meaningful when it fires.
  const handleSave = async () => {
    if (isBlank && !window.confirm(t("annualPlanner.confirmDeleteNote"))) {
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    try {
      if (isBlank) {
        await deleteNote();
        onDeleted?.();
      } else {
        await saveNote();
        onSaved?.();
      }
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  return { noteText, setNoteText, isSaving, errorMessage, canSave, handleSave };
};
